import { useContext } from "react";
import type { CSSProperties, ReactElement } from "react";
import { Link } from "react-router-dom";
import { ResourceArticleMetaContext } from "../newborn-pages/ResourceArticleMetaContext";
import "./ArticleHero.scss";

export interface ArticleBreadcrumb {
  label: string;
  to?: string;
}

interface ArticleHeroProps {
  breadcrumbs: ArticleBreadcrumb[];
  category: string;
  description: string;
  imageAlt: string;
  imagePosition?: string;
  imageSrc: string;
  readingTime: string;
  title: string;
  updatedAt: string;
  updatedLabel: string;
}

type ArticleHeroStyle = CSSProperties & {
  "--article-hero-image-position": string;
};

export const ArticleHero = ({
  breadcrumbs,
  category,
  description,
  imageAlt,
  imagePosition = "center",
  imageSrc,
  readingTime,
  title,
  updatedAt,
  updatedLabel,
}: ArticleHeroProps): ReactElement => {
  const articleMeta = useContext(ResourceArticleMetaContext);
  const heroStyle: ArticleHeroStyle = {
    "--article-hero-image-position": imagePosition,
  };
  const resolvedUpdatedAt = articleMeta?.updatedAt ?? articleMeta?.publishedAt ?? updatedAt;
  const resolvedUpdatedLabel = articleMeta
    ? `${articleMeta.updatedAt ? "Updated" : "Published"} ${new Intl.DateTimeFormat("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(`${resolvedUpdatedAt}T12:00:00`))}`
    : updatedLabel;
  const resolvedReadingTime = articleMeta?.readTime ?? readingTime;

  return (
    <header className="resource-article-hero" style={heroStyle}>
      <img
        alt={imageAlt}
        className="resource-article-hero__image"
        decoding="async"
        {...({ fetchpriority: "high" } as Record<string, string>)}
        loading="eager"
        src={imageSrc}
      />

      <div aria-hidden="true" className="resource-article-hero__overlay" />

      <div className="newborn-shell resource-article-hero__content">
        <nav aria-label="Breadcrumb" className="resource-article-hero__breadcrumb">
          {breadcrumbs.map((item, index) => (
            <span className="resource-article-hero__breadcrumb-item" key={`${item.label}-${index}`}>
              {item.to ? <Link to={item.to}>{item.label}</Link> : <span>{item.label}</span>}
              {index < breadcrumbs.length - 1 && <span aria-hidden="true">/</span>}
            </span>
          ))}
        </nav>

        <p className="resource-article-hero__category">{category}</p>
        <h1>{title}</h1>
        <p className="resource-article-hero__description">{description}</p>

        <div className="resource-article-hero__meta">
          <time dateTime={resolvedUpdatedAt}>{resolvedUpdatedLabel}</time>
          <span aria-hidden="true">•</span>
          <span>{resolvedReadingTime}</span>
        </div>
      </div>
    </header>
  );
};

export default ArticleHero;
